import type { DataProvider, RaRecord } from "ra-core";

import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import type { AttachmentNote } from "../../types";
import { getSupabaseClient } from "./supabase";

// Volt: the bucket attachments is private, so a stored src (public URL from
// upload time) no longer opens. Every read of notes, and of the activity log
// that embeds notes, gets short-lived signed URLs, signed by path in one
// request per page. Kept out of dataProvider.ts to ease upstream merges.

const RESOURCES = new Set(["contact_notes", "deal_notes", "activity_log"]);
const NOTE_RESOURCES = new Set(["contact_notes", "deal_notes"]);
const SIGNED_URL_TTL_SECONDS = 60 * 60;

type WithAttachments = { attachments?: AttachmentNote[] | null };

// Notes carry attachments themselves; activity_log rows embed a note as an
// object column (contact_note, deal_note).
const attachmentHolders = (record: RaRecord): WithAttachments[] => [
  record as WithAttachments,
  ...Object.values(record).filter(
    (value): value is WithAttachments =>
      value != null &&
      typeof value === "object" &&
      Array.isArray((value as WithAttachments).attachments),
  ),
];

// Older rows have no path; it is the last segment of the public URL.
const pathOf = (attachment: AttachmentNote) =>
  attachment.path ??
  attachment.src?.split(`/object/public/${ATTACHMENTS_BUCKET}/`)[1];

const signRecords = async <T extends RaRecord>(records: T[]): Promise<T[]> => {
  const holders = records.flatMap(attachmentHolders);
  const paths = [
    ...new Set(
      holders
        .flatMap((holder) => holder.attachments ?? [])
        .map(pathOf)
        .filter((path): path is string => Boolean(path)),
    ),
  ];
  if (!paths.length) return records;

  const { data, error } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);
  if (error || !data) {
    console.error("createSignedUrls.error", error);
    return records;
  }
  const signed = new Map(
    data
      .filter((entry) => entry.signedUrl && entry.path)
      .map((entry) => [entry.path as string, entry.signedUrl]),
  );

  for (const holder of holders) {
    if (!holder.attachments) continue;
    holder.attachments = holder.attachments.map((attachment) => {
      const path = pathOf(attachment);
      const src = path ? signed.get(path) : undefined;
      return src ? { ...attachment, path, src } : attachment;
    });
  }
  return records;
};

// A note opened for editing carries signed URLs; store the stable public form
// instead, so no expiring token ends up in the database. Reads sign by path.
const publicSrc = (path: string) =>
  `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/${ATTACHMENTS_BUCKET}/${path}`;

const unsign = <T extends Partial<WithAttachments>>(data: T): T => {
  if (!Array.isArray(data?.attachments)) return data;
  return {
    ...data,
    attachments: data.attachments.map((attachment) =>
      attachment.path && attachment.src?.includes("/object/sign/")
        ? { ...attachment, src: publicSrc(attachment.path) }
        : attachment,
    ),
  };
};

export const withSignedAttachments = <T extends DataProvider>(
  dataProvider: T,
): T => ({
  ...dataProvider,
  getList: async (resource, params) => {
    const result = await dataProvider.getList(resource, params);
    if (!RESOURCES.has(resource)) return result;
    return { ...result, data: await signRecords(result.data) };
  },
  getManyReference: async (resource, params) => {
    const result = await dataProvider.getManyReference(resource, params);
    if (!RESOURCES.has(resource)) return result;
    return { ...result, data: await signRecords(result.data) };
  },
  getMany: async (resource, params) => {
    const result = await dataProvider.getMany(resource, params);
    if (!RESOURCES.has(resource)) return result;
    return { ...result, data: await signRecords(result.data) };
  },
  create: (resource, params) =>
    dataProvider.create(
      resource,
      NOTE_RESOURCES.has(resource)
        ? { ...params, data: unsign(params.data) }
        : params,
    ),
  update: (resource, params) =>
    dataProvider.update(
      resource,
      NOTE_RESOURCES.has(resource)
        ? { ...params, data: unsign(params.data) }
        : params,
    ),
  getOne: async (resource, params) => {
    const result = await dataProvider.getOne(resource, params);
    if (!RESOURCES.has(resource)) return result;
    const [data] = await signRecords([result.data]);
    return { ...result, data };
  },
});
